/**
 * aiApi.ts — request/response shapes of the CMS Worker's /ai/* routes, shared
 * with the admin UI. (The CMS <-> AI Worker contract is in ai.ts; this file is
 * the CMS <-> browser contract.)
 */
import { z } from "zod";
import { draftRejectTagSchema } from "./feedback.ts";
import { webCheckSchema } from "./webcheck.ts";
import {
  aiSourceSchema,
  draftResponseSchema,
  ideaSignalSchema,
  ideasResponseSchema,
  topicIdeaSchema,
  aiProviderSchema,
} from "./ai.ts";

export const PROPOSAL_KINDS = ["topic_ideas", "article_draft"] as const;
export const proposalKindSchema = z.enum(PROPOSAL_KINDS);
export const proposalStatusSchema = z.enum(["pending", "approved", "rejected", "applied", "stale", "failed"]);
export type ProposalStatus = z.infer<typeof proposalStatusSchema>;

export const plagiarismReportSchema = z.object({
  overlapPercent: z.number(),
  longestCopiedRunWords: z.number(),
  topMatches: z.array(z.object({ id: z.string(), title: z.string(), percent: z.number() })),
  /** Added later: stored reports from before it have none. */
  passages: z.array(z.object({ text: z.string(), words: z.number(), sourceId: z.string(), sourceTitle: z.string() })).default([]),
  level: z.enum(["ok", "review", "high", "not_enough_text"]),
  draftWords: z.number(),
  referencesChecked: z.number(),
});

/** How a draft's claims fare against the sources it was given (computed by the CMS, not trusted from the model). */
export const claimSummarySchema = z.object({
  total: z.number().int(),
  sourced: z.number().int(),
  unsourced: z.number().int(),
  /** Unsourced claims of kinds that must not go live unchecked (statistics, dates, medical/legal). */
  unsourcedHighRisk: z.number().int(),
});
export type ClaimSummary = z.infer<typeof claimSummarySchema>;

export const aiProposalSchema = z.object({
  id: z.string(),
  siteId: z.string(),
  kind: proposalKindSchema,
  status: proposalStatusSchema,
  provider: aiProviderSchema,
  model: z.string(),
  /** What the human asked for (seed/topic/sources). Never contains secrets. */
  input: z.unknown(),
  /** Validated model output: IdeasResponse for topic_ideas, DraftResponse for article_draft. */
  output: z.unknown(),
  contentId: z.string().nullable(),
  baseHash: z.string().nullable(),
  plagiarism: plagiarismReportSchema.nullable(),
  claimSummary: claimSummarySchema.nullable(),
  /** "Check the web" (Copyscape) result, or null when nobody has run it. */
  webCheck: webCheckSchema.nullable().default(null),
  /** topic_ideas only: manager decision per idea index ("0" -> approved). Derived from memory events. */
  ideaDecisions: z.record(z.string(), z.enum(["approved", "rejected"])).default({}),
  /** topic_ideas only: the optional reason and comment given with each decision, by idea index. */
  ideaNotes: z.record(z.string(), z.object({ reason: z.string().nullable(), note: z.string().nullable() })).default({}),
  inputTokens: z.number().int(),
  outputTokens: z.number().int(),
  /** US dollars this call cost; null = the model has no price yet (never 0). */
  costUsd: z.number().nullable().default(null),
  createdBy: z.string().nullable(),
  decidedBy: z.string().nullable(),
  decidedAt: z.string().nullable(),
  decisionNote: z.string().nullable(),
  createdAt: z.string(),
});
export type AiProposal = z.infer<typeof aiProposalSchema>;

export const listProposalsResponseSchema = z.object({
  items: z.array(aiProposalSchema),
  /** Total matching the filters (all pages). */
  total: z.number().int().min(0),
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1),
});
export type ListProposalsResponse = z.infer<typeof listProposalsResponseSchema>;

export const listProposalsQuerySchema = z.object({
  siteId: z.string().min(1),
  kind: z.enum(["topic_ideas", "article_draft"]).optional(),
  status: z.enum(["pending", "approved", "rejected", "applied", "stale", "failed"]).optional(),
  /** Free-text search over what was asked and what the AI answered. */
  q: z.string().trim().max(100).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(50).default(10),
});
export type ListProposalsQuery = z.infer<typeof listProposalsQuerySchema>;

/** Proposals in these states are the audit trail of what a human decided and are never deletable. */
export const KEPT_PROPOSAL_STATUSES = ["approved", "applied"] as const;

// --- requests ---

export const generateIdeasBodySchema = z.object({
  siteId: z.string().min(1),
  seed: z.string().trim().min(2).max(300),
  audience: z.string().trim().max(300).optional(),
  signals: z.array(ideaSignalSchema).max(100).default([]),
  count: z.number().int().min(1).max(20).default(10),
});
export type GenerateIdeasBody = z.infer<typeof generateIdeasBodySchema>;

export const generateDraftBodySchema = z.object({
  siteId: z.string().min(1),
  topic: z.object({
    title: z.string().trim().min(5).max(200),
    targetKeyword: z.string().trim().min(2).max(200),
    angle: z.string().trim().max(500).optional(),
    questions: z.array(z.string().max(300)).max(12).default([]),
  }),
  audience: z.string().trim().max(300).optional(),
  /** The ONLY material the draft may cite. */
  sources: z.array(aiSourceSchema).max(12).default([]),
  targetWords: z.number().int().min(300).max(4000).default(1500),
  /** The ideas proposal this topic came from, for provenance. */
  sourceProposalId: z.string().optional(),
  /** Regenerate for an existing item (it must be in AI-assist mode). */
  contentId: z.string().optional(),
});
export type GenerateDraftBody = z.infer<typeof generateDraftBodySchema>;

/** Quick reasons for a topic decision. Optional: a person may give one, a comment, both or neither. */
export const IDEA_REASONS = {
  approved: ["Good fit for our readers", "People are searching for this", "Matches what we sell", "Good timing"],
  rejected: ["Off topic for us", "Already covered", "Too broad", "Wrong audience", "Not what we sell", "Bad timing"],
} as const;
export const decideIdeaBodySchema = z.object({
  ideaIndex: z.number().int().min(0).max(19),
  decision: z.enum(["approved", "rejected"]),
  /** One of the quick reasons above (free text is accepted so old clients keep working). */
  reason: z.string().trim().max(60).optional(),
  note: z.string().trim().max(300).optional(),
});
export type DecideIdeaBody = z.infer<typeof decideIdeaBodySchema>;

export const approveDraftBodySchema = z.object({
  /** Required by the site's templates for articles (see getContentPublishBlockers). */
  authorId: z.string().min(1),
  categoryId: z.string().min(1).nullable().default(null),
  note: z.string().trim().max(300).optional(),
});
export type ApproveDraftBody = z.infer<typeof approveDraftBodySchema>;

export const rejectProposalBodySchema = z.object({
  note: z.string().trim().max(500).optional(),
  /** Why it was rejected; repeated reasons become suggested rules (see feedback.ts). */
  tags: z.array(draftRejectTagSchema).max(8).default([]),
});

export const approveDraftResponseSchema = z.object({ proposal: aiProposalSchema, contentId: z.string() });

// --- handoff ---

export const handoffStateResponseSchema = z.object({
  contentId: z.string(),
  owner: z.enum(["ai", "human"]),
  mode: z.enum(["ai_assist", "human", "paused"]),
  leaseUserId: z.string().nullable(),
  leaseExpiresAt: z.string().nullable(),
  leaseActive: z.boolean(),
  /** True when the signed-in user is the current lease holder. */
  heldByMe: z.boolean(),
});
export type HandoffStateResponse = z.infer<typeof handoffStateResponseSchema>;

/** The editing hold as the content editor sees it: the handoff state plus who holds it (for "Mia is editing"). */
export const editLockResponseSchema = handoffStateResponseSchema.extend({
  holderName: z.string().nullable(),
  /** True when someone else's active hold blocks this person's changes. */
  lockedByOther: z.boolean(),
});
export type EditLockResponse = z.infer<typeof editLockResponseSchema>;

// --- memory ---

export const memoryRuleSchema = z.object({
  id: z.string(),
  siteId: z.string(),
  text: z.string(),
  status: z.enum(["proposed", "approved", "rejected"]),
  source: z.enum(["manager", "edit_pattern"]),
  createdBy: z.string().nullable(),
  decidedBy: z.string().nullable(),
  decidedAt: z.string().nullable(),
  createdAt: z.string(),
});
export type MemoryRule = z.infer<typeof memoryRuleSchema>;
export const listMemoryRulesResponseSchema = z.object({ items: z.array(memoryRuleSchema) });
export const createMemoryRuleBodySchema = z.object({ siteId: z.string().min(1), text: z.string().trim().min(5).max(500) });
export const decideMemoryRuleBodySchema = z.object({ decision: z.enum(["approved", "rejected"]) });

export const memorySummarySchema = z.object({
  approvedRules: z.number().int(),
  proposedRules: z.number().int(),
  preferredTopics: z.array(z.string()),
  avoidedTopics: z.array(z.string()),
});

// --- health / usage ---

export const aiStatusResponseSchema = z.object({
  configured: z.boolean(),
  /** Providers the AI Worker has a key for; null when it could not be reached. */
  providers: z.array(z.object({ provider: aiProviderSchema, configured: z.boolean(), defaultModel: z.string().nullable() })).nullable(),
  defaultProvider: aiProviderSchema.nullable(),
  siteProvider: aiProviderSchema,
  siteModel: z.string().nullable(),
  error: z.string().nullable(),
  usageThisMonth: z.object({ proposals: z.number().int(), inputTokens: z.number().int(), outputTokens: z.number().int() }),
});
export type AiStatusResponse = z.infer<typeof aiStatusResponseSchema>;

export const plagiarismRequestBodySchema = z.object({
  contentId: z.string().min(1),
  /** The body as currently typed in the editor (possibly unsaved). When omitted the saved body is checked. */
  text: z.string().max(400_000).optional(),
});
export const plagiarismResponseSchema = plagiarismReportSchema;

// Re-exported so the admin can parse proposal outputs without importing ai.ts separately.
export { ideasResponseSchema, draftResponseSchema, topicIdeaSchema };
